import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { LoanPreAgreementData } from '../../../services/schemas/loanServiceSchemas/loanPreAgreementData';

interface PreAgreementFormProps {
    initialData: LoanPreAgreementData;
    onSubmit: (data: LoanPreAgreementData) => void;
    onCancel: () => void;
    onSkip?: () => void;
    isGenerating?: boolean;
}

const PreAgreementForm: React.FC<PreAgreementFormProps> = ({
    initialData,
    onSubmit,
    onCancel,
    onSkip,
    isGenerating = false
}) => {
    const [formData, setFormData] = useState<LoanPreAgreementData>(initialData);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        setFormData(initialData);
    }, [initialData]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        
        // Handle numeric fields
        if (['creditAdvanced', 'initiationFee', 'totalAdditionalCharges', 'deductDeposit', 
             'totalAmountDeferred', 'installmentAmount', 'monthlyServiceFee', 'monthlyRequiredInsurance',
             'numberOfInstallments', 'totalInstallment', 'totalAmountDeferredPerAgreement', 
             'totalInterest', 'totalAmountRepayable', 'additionalMonthlyPremium', 'annualInterestRate',
             'initiationFeeNumber1', 'serviceFee', 'totalOfCharges'].includes(name)) {
            
            const numericValue = value === '' ? undefined : parseFloat(value);
            setFormData(prev => ({
                ...prev,
                [name]: numericValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value || undefined
            }));
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Required fields
        if (!formData.customerFullName?.trim()) {
            newErrors.customerFullName = 'Customer full name is required';
        }

        if (!formData.creditAdvanced || formData.creditAdvanced <= 0) {
            newErrors.creditAdvanced = 'Credit advanced must be greater than 0';
        }

        if (!formData.loanApplicationId?.trim()) {
            newErrors.loanApplicationId = 'Loan application ID is required';
        }

        // Validate percentage fields
        // The rate entered on the loan application, as it is (finding #18): only a negative rate is refused.
        if (formData.annualInterestRate !== undefined && formData.annualInterestRate < 0) {
            newErrors.annualInterestRate = 'Annual interest rate cannot be negative';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (validateForm()) {
            onSubmit(formData);
        } else {
            toast.error('Please correct the errors in the form');
        }
    };

    const installmentFrequencyOptions = [
        { value: 'MONTHLY', label: 'Monthly' },
        { value: 'FIFTEEN_DAYS', label: 'Every 15 Days' },
        { value: 'BI_MONTHLY', label: 'Every 2 Months' },
        { value: 'WEEKLY', label: 'Weekly' },
        { value: 'DAILY', label: 'Daily' },
        { value: 'QUARTERLY', label: 'Quarterly' },
        { value: 'CUSTOM', label: 'Custom' }
    ];
    // Preparation returns display labels; keep the original value in the payload.
    const selectedFrequency = installmentFrequencyOptions.find(option =>
        option.value === formData.installmentFrequency ||
        option.label.toLowerCase() === formData.installmentFrequency?.toLowerCase()
    )?.value ?? formData.installmentFrequency ?? '';

    return (
        <>
            <h6 className="form-section-title">Pre-Agreement Data Verification</h6>
            <p className="text-muted mb-4">
                Review and edit the pre-agreement data below. All fields marked with * are required.
            </p>

            <form onSubmit={handleSubmit}>
                <div className="row g-3">
                    {/* Customer Information Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Customer Information</h6>
                        <div className="row g-3">
                                <div className="col-12">
                                    <div className="form-group">
                                        <label htmlFor="customerFullName">Customer Full Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.customerFullName ? 'is-invalid' : ''}`}
                                            id="customerFullName"
                                            name="customerFullName"
                                            value={formData.customerFullName || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter customer full name"
                                            disabled={isGenerating}
                                        />
                                        {errors.customerFullName && (
                                            <div className="invalid-feedback">{errors.customerFullName}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="customerContactNumber">Customer Contact Number</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.customerContactNumber ? 'is-invalid' : ''}`}
                                            id="customerContactNumber"
                                            name="customerContactNumber"
                                            value={formData.customerContactNumber || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter customer contact number"
                                            disabled={isGenerating}
                                        />
                                        {errors.customerContactNumber && (
                                            <div className="invalid-feedback">{errors.customerContactNumber}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="registrationNumber">Registration Number</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.registrationNumber ? 'is-invalid' : ''}`}
                                            id="registrationNumber"
                                            name="registrationNumber"
                                            value={formData.registrationNumber || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter registration number"
                                            disabled={isGenerating}
                                        />
                                        {errors.registrationNumber && (
                                            <div className="invalid-feedback">{errors.registrationNumber}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="preAgreementDate">Pre-Agreement Date</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.preAgreementDate ? 'is-invalid' : ''}`}
                                            id="preAgreementDate"
                                            name="preAgreementDate"
                                            value={formData.preAgreementDate || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter pre-agreement date"
                                            disabled={isGenerating}
                                        />
                                        {errors.preAgreementDate && (
                                            <div className="invalid-feedback">{errors.preAgreementDate}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="applicationNumber">Application Number</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.applicationNumber ? 'is-invalid' : ''}`}
                                            id="applicationNumber"
                                            name="applicationNumber"
                                            value={formData.applicationNumber || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter application number"
                                            disabled={isGenerating}
                                        />
                                        {errors.applicationNumber && (
                                            <div className="invalid-feedback">{errors.applicationNumber}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="form-group">
                                        <label htmlFor="physicalAddress">Physical Address</label>
                                        <textarea
                                            className={`form-control ${errors.physicalAddress ? 'is-invalid' : ''}`}
                                            id="physicalAddress"
                                            name="physicalAddress"
                                            value={formData.physicalAddress || ''}
                                            onChange={handleInputChange}
                                            rows={3}
                                            placeholder="Enter physical address"
                                            disabled={isGenerating}
                                        />
                                        {errors.physicalAddress && (
                                            <div className="invalid-feedback">{errors.physicalAddress}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                    </div>

                    {/* Provider Information Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Provider Information</h6>
                        <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="contactNumberOfProvider">Contact Number of Provider</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.contactNumberOfProvider ? 'is-invalid' : ''}`}
                                            id="contactNumberOfProvider"
                                            name="contactNumberOfProvider"
                                            value={formData.contactNumberOfProvider || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter provider contact number"
                                            disabled={isGenerating}
                                        />
                                        {errors.contactNumberOfProvider && (
                                            <div className="invalid-feedback">{errors.contactNumberOfProvider}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                    </div>

                    {/* Loan Amount Details Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Loan Amount Details</h6>
                        <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="creditAdvanced">Credit Advanced (R) <span className="text-danger">*</span></label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.creditAdvanced ? 'is-invalid' : ''}`}
                                            id="creditAdvanced"
                                            name="creditAdvanced"
                                            value={formData.creditAdvanced ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter credit advanced amount"
                                            disabled={isGenerating}
                                        />
                                        {errors.creditAdvanced && (
                                            <div className="invalid-feedback">{errors.creditAdvanced}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="initiationFee">Initiation Fee (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.initiationFee ? 'is-invalid' : ''}`}
                                            id="initiationFee"
                                            name="initiationFee"
                                            value={formData.initiationFee ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter initiation fee"
                                            disabled={isGenerating}
                                        />
                                        {errors.initiationFee && (
                                            <div className="invalid-feedback">{errors.initiationFee}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="totalAdditionalCharges">Total Additional Charges (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.totalAdditionalCharges ? 'is-invalid' : ''}`}
                                            id="totalAdditionalCharges"
                                            name="totalAdditionalCharges"
                                            value={formData.totalAdditionalCharges ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter total additional charges"
                                            disabled={isGenerating}
                                        />
                                        {errors.totalAdditionalCharges && (
                                            <div className="invalid-feedback">{errors.totalAdditionalCharges}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="deductDeposit">Deduct Deposit (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.deductDeposit ? 'is-invalid' : ''}`}
                                            id="deductDeposit"
                                            name="deductDeposit"
                                            value={formData.deductDeposit ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter deduct deposit amount"
                                            disabled={isGenerating}
                                        />
                                        {errors.deductDeposit && (
                                            <div className="invalid-feedback">{errors.deductDeposit}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="totalAmountDeferred">Total Amount Deferred (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.totalAmountDeferred ? 'is-invalid' : ''}`}
                                            id="totalAmountDeferred"
                                            name="totalAmountDeferred"
                                            value={formData.totalAmountDeferred ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter total amount deferred"
                                            disabled={isGenerating}
                                        />
                                        {errors.totalAmountDeferred && (
                                            <div className="invalid-feedback">{errors.totalAmountDeferred}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="totalAmountDeferredPerAgreement">Total Amount Deferred Per Agreement (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.totalAmountDeferredPerAgreement ? 'is-invalid' : ''}`}
                                            id="totalAmountDeferredPerAgreement"
                                            name="totalAmountDeferredPerAgreement"
                                            value={formData.totalAmountDeferredPerAgreement ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter total amount deferred per agreement"
                                            disabled={isGenerating}
                                        />
                                        {errors.totalAmountDeferredPerAgreement && (
                                            <div className="invalid-feedback">{errors.totalAmountDeferredPerAgreement}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                    </div>

                    {/* Installment Details Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Installment Details</h6>
                        <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="installmentAmount">Installment Amount (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.installmentAmount ? 'is-invalid' : ''}`}
                                            id="installmentAmount"
                                            name="installmentAmount"
                                            value={formData.installmentAmount ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter installment amount"
                                            disabled={isGenerating}
                                        />
                                        {errors.installmentAmount && (
                                            <div className="invalid-feedback">{errors.installmentAmount}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="numberOfInstallments">Number of Installments</label>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.numberOfInstallments ? 'is-invalid' : ''}`}
                                            id="numberOfInstallments"
                                            name="numberOfInstallments"
                                            value={formData.numberOfInstallments ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter number of installments"
                                            disabled={isGenerating}
                                        />
                                        {errors.numberOfInstallments && (
                                            <div className="invalid-feedback">{errors.numberOfInstallments}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="installmentFrequency">Installment Frequency</label>
                                        <select
                                            className={`form-select ${errors.installmentFrequency ? 'is-invalid' : ''}`}
                                            id="installmentFrequency"
                                            name="installmentFrequency"
                                            value={selectedFrequency}
                                            onChange={handleInputChange}
                                            disabled={isGenerating}
                                        >
                                            <option value="">Select installment frequency</option>
                                            {selectedFrequency && !installmentFrequencyOptions.some(option => option.value === selectedFrequency) && (
                                                <option value={selectedFrequency}>{selectedFrequency}</option>
                                            )}
                                            {installmentFrequencyOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.installmentFrequency && (
                                            <div className="invalid-feedback">{errors.installmentFrequency}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="totalInstallment">Total Installment (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.totalInstallment ? 'is-invalid' : ''}`}
                                            id="totalInstallment"
                                            name="totalInstallment"
                                            value={formData.totalInstallment ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter total installment"
                                            disabled={isGenerating}
                                        />
                                        {errors.totalInstallment && (
                                            <div className="invalid-feedback">{errors.totalInstallment}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="monthlyServiceFee">Monthly Service Fee (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.monthlyServiceFee ? 'is-invalid' : ''}`}
                                            id="monthlyServiceFee"
                                            name="monthlyServiceFee"
                                            value={formData.monthlyServiceFee ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter monthly service fee"
                                            disabled={isGenerating}
                                        />
                                        {errors.monthlyServiceFee && (
                                            <div className="invalid-feedback">{errors.monthlyServiceFee}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="monthlyRequiredInsurance">Monthly Required Insurance (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.monthlyRequiredInsurance ? 'is-invalid' : ''}`}
                                            id="monthlyRequiredInsurance"
                                            name="monthlyRequiredInsurance"
                                            value={formData.monthlyRequiredInsurance ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter monthly required insurance"
                                            disabled={isGenerating}
                                        />
                                        {errors.monthlyRequiredInsurance && (
                                            <div className="invalid-feedback">{errors.monthlyRequiredInsurance}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                    </div>

                    {/* Cost and Interest Details Section */}
                    <div className="col-12">
                        <h6 className="form-section-title">Cost and Interest Details</h6>
                        <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="totalInterest">Total Interest (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.totalInterest ? 'is-invalid' : ''}`}
                                            id="totalInterest"
                                            name="totalInterest"
                                            value={formData.totalInterest ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter total interest"
                                            disabled={isGenerating}
                                        />
                                        {errors.totalInterest && (
                                            <div className="invalid-feedback">{errors.totalInterest}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="totalAmountRepayable">Total Amount Repayable (R)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.totalAmountRepayable ? 'is-invalid' : ''}`}
                                            id="totalAmountRepayable"
                                            name="totalAmountRepayable"
                                            value={formData.totalAmountRepayable ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter total amount repayable"
                                            disabled={isGenerating}
                                        />
                                        {errors.totalAmountRepayable && (
                                            <div className="invalid-feedback">{errors.totalAmountRepayable}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="annualInterestRate">Annual Interest Rate (%)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className={`form-control ${errors.annualInterestRate ? 'is-invalid' : ''}`}
                                            id="annualInterestRate"
                                            name="annualInterestRate"
                                            value={formData.annualInterestRate ?? ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter annual interest rate"
                                            disabled={isGenerating}
                                        />
                                        {errors.annualInterestRate ? (
                                            <div className="invalid-feedback">{errors.annualInterestRate}</div>
                                        ) : (
                                            <small className="form-text text-muted">
                                                The interest rate entered on the loan application.
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>
                    </div>

                    {/* Form Status and Actions */}
                    <div className="col-12">
                            {/* Data Status Alerts */}
                            {formData.validationWarnings && formData.validationWarnings.length > 0 && (
                                <div className="alert alert-warning mb-3">
                                    <h6 className="alert-heading">Validation Warnings</h6>
                                    <ul className="mb-0">
                                        {formData.validationWarnings.map((warning, index) => (
                                            <li key={index}>{warning}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {formData.missingOptionalFields && formData.missingOptionalFields.length > 0 && (
                                <div className="alert alert-info mb-3">
                                    <h6 className="alert-heading">Missing Optional Fields</h6>
                                    <ul className="mb-0">
                                        {formData.missingOptionalFields.map((field, index) => (
                                            <li key={index}>{field}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Form Completion Status */}
                            <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded mb-4">
                                <div>
                                    <strong>Form Completion Status:</strong>
                                    <span className="ms-2">
                                        {formData.fieldsPopulated || 0} of {formData.totalAvailableFields || 0} fields populated
                                    </span>
                                </div>
                                <div>
                                    <span className={`badge ${formData.canGenerate ? 'bg-success' : 'bg-warning'}`}>
                                        {formData.canGenerate ? 'Ready to Generate' : 'Incomplete Data'}
                                    </span>
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="d-flex justify-content-between gap-2">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onCancel}
                                    disabled={isGenerating}
                                >
                                    <i className="fa fa-times me-2"></i>
                                    Cancel
                                </button>
                                <div className="d-flex gap-2">
                                    {onSkip && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={onSkip}
                                            disabled={isGenerating}
                                        >
                                            <i className="fa fa-forward me-2"></i>
                                            Skip Generation
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isGenerating}
                                    >
                                        {isGenerating ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Generating PDF...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-file-pdf me-2"></i>
                                                Generate Pre-Agreement PDF
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                    </div>
                </div>
            </form>
        </>
    );
};

export default PreAgreementForm;
